import { useState, useMemo, useCallback } from "react"
import { View, useWindowDimensions, Pressable, ActivityIndicator } from "react-native"
import { Link, useLocalSearchParams } from "expo-router"
import { Header } from "components/Header"
import { List } from "components/list"
import { trpc } from "utils/trpc"
import { ar } from "i18n/ar"
import { dayjs } from "utils/dayjs"
import { formatDateForDisplay } from "utils/dayjs"
import { formatPrice, sharedStyles } from "utils"
import { Badge } from "components/ui/badge"
import { Text } from "components/ui/typography"
import { ModalWrapper } from "components/core"
import { SafeAreaView } from "react-native-safe-area-context"
import Icon from "@react-native-vector-icons/material-design-icons"
import { Show, useObservable } from "@legendapp/state/react"

import { TransactionFilters } from "components/transactions/transaction-filters"

export default function TransactionsScreen() {
  const params = useLocalSearchParams<{ supplierId: string }>()
  const { width } = useWindowDimensions()
  const total$ = useObservable(false)

  const [fromDate, setFromDate] = useState<number | undefined>(dayjs().startOf("day").utc().unix())
  const [toDate, setToDate] = useState<number | undefined>(dayjs().endOf("day").utc().unix())

  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>(params.supplierId ? [Number(params.supplierId)] : [])
  const [status, setStatus] = useState<"active" | "cancelled">("active")

  function hideTotal() {
    total$.set(false)
  }

  // Responsive columns: 1 on mobile, 2 on tablet, 3 on desktop
  const numColumns = useMemo(() => {
    if (width >= 1024) return 3
    if (width >= 768) return 2
    return 1
  }, [width])

  const transactions = trpc.transactions.list.useInfiniteQuery(
    { fromDate, toDate, supplierId: selectedSuppliers[0], status },
    { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined },
  )

  // Flatten paginated data for rendering
  const allTransactions = useMemo(() => transactions.data?.pages.flatMap((page) => page.items) ?? [], [transactions.data])

  const handleEndReached = useCallback(() => {
    if (transactions.hasNextPage && !transactions.isFetchingNextPage) {
      transactions.fetchNextPage()
    }
  }, [transactions])

  // const ListHeaderComponent = () => (
  // )

  const ListEmptyComponent = () =>
    !transactions.isLoading ? (
      <View className="p-8 items-center bg-white rounded-lg border border-gray-200 border-dashed mt-4">
        <Icon name="receipt" size={48} color="#9CA3AF" />
        <Text className="mt-2 text-gray-500">{ar.noTransactionsFound}</Text>
      </View>
    ) : null

  const ListFooterComponent = () => (
    <View>
      {transactions.isFetchingNextPage && (
        <View className="py-4 items-center">
          <ActivityIndicator size="small" color="#2563EB" />
        </View>
      )}
      <SafeAreaView edges={["bottom"]} />
    </View>
  )

  return (
    <>
      <Header
        title={ar.transactions}
        right={
          <View className="flex-row h-full gapx-1">
            <Pressable className="h-full px-2 justify-center active:opacity-70" onPress={() => total$.set(true)}>
              <Icon name="sigma" size={24} color="blue" />
            </Pressable>

            <Link href="/dashboard/transactions/create" asChild>
              <Pressable className="h-full px-2 justify-center active:opacity-70">
                <Icon name="plus" size={24} color="blue" />
              </Pressable>
            </Link>

            <Pressable className="h-full px-2 justify-center active:opacity-70" onPress={() => transactions.refetch()}>
              <Icon name="refresh" size={24} color="blue" />
            </Pressable>
          </View>
        }
      />

      <List
        key={numColumns}
        data={allTransactions}
        keyExtractor={(item) => `${item.id}`}
        renderItem={({ item }) => {
          let statusBadge = null
          const isReturn = item.type === "return"

          if (item.status === "cancelled") {
            statusBadge = <Badge variant="destructive">{ar.cancelled}</Badge>
          } else if (item.status === "edited") {
            statusBadge = <Badge variant="secondary">{ar.transactionEdited}</Badge>
          }

          const returnBadge = isReturn ? (
            <Badge variant="outline" className="border-orange-500 text-orange-600 bg-orange-50">
              {ar.returnTransaction}
            </Badge>
          ) : null

          return (
            <Link href={`/dashboard/transactions/${item.id}`} asChild>
              <Pressable className="active:opacity-80 p-1">
                <View className={`border-l-4 ${isReturn ? "border-l-orange-500" : "border-l-blue-500"} h-full bg-white rounded-sm`}>
                  <View className="p-3 gap-2">
                    <View className="flex-row justify-between items-center">
                      <View className="flex-row items-center gap-2">
                        <Text className="font-bold text-gray-900">#{item.id}</Text>
                        <Text variant="muted" className="text-xs">
                          {formatDateForDisplay(item.time)}
                        </Text>
                      </View>
                      <View className="flex-row gap-2">
                        {returnBadge}
                        {statusBadge}
                      </View>
                    </View>
                    <Text className="font-semibold text-base text-gray-800">{item.supplier?.name || ar.noSupplier}</Text>
                    <View className="flex-row items-center gap-3 flex-wrap">
                      {item.storagePlace && (
                        <View className="flex-row items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                          <Icon name="map-marker" size={14} color="#6B7280" />
                          <Text variant="small" className="text-gray-600">
                            {item.storagePlace.name}
                          </Text>
                        </View>
                      )}
                      <View className="flex-row items-center gap-1 bg-blue-50 px-2 py-1 rounded">
                        <Icon name="package-variant" size={14} color="#2563EB" />
                        <Text variant="small" className="text-blue-700 font-medium">
                          {item.products.length} {ar.products}
                        </Text>
                      </View>
                    </View>

                    <View className="flex-row justify-end">
                      <Text className="font-bold text-lg text-green-600">{formatPrice(item.total)}</Text>
                    </View>
                  </View>
                </View>
              </Pressable>
            </Link>
          )
        }}
        numColumns={numColumns}
        ListHeaderComponent={
          <TransactionFilters
            fromDate={fromDate}
            toDate={toDate}
            selectedSuppliers={selectedSuppliers}
            status={status}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            onSuppliersChange={setSelectedSuppliers}
            onStatusChange={(val) => setStatus(val)}
          />
        }
        ListEmptyComponent={ListEmptyComponent}
        onRefresh={transactions.refetch}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
        contentContainerStyle={sharedStyles.contentContainer}
        ListFooterComponent={ListFooterComponent}
      />

      <Show if={total$}>
        {() => <TransactionsTotal hide={hideTotal} fromDate={fromDate} toDate={toDate} supplierId={selectedSuppliers[0]} status={status} />}
      </Show>
    </>
  )
}

type TransactionsTotalProps = {
  hide: () => void
  fromDate: number | undefined
  toDate: number | undefined
  supplierId: number | undefined
  status: "active" | "cancelled"
}

function TransactionsTotal({ hide, fromDate, toDate, supplierId, status }: TransactionsTotalProps) {
  // Fires on mount (component only renders while the modal is open via <Show>)
  const total = trpc.transactions.total.useQuery({ fromDate, toDate, supplierId, status })
  const net = (total.data?.purchases ?? 0) - (total.data?.returns ?? 0)
  const netColor = net >= 0 ? "text-green-600" : "text-red-600"

  return (
    <ModalWrapper onClose={hide} className="p-1.5">
      <View className="flex-row items-center justify-between mb-4">
        <Text className="text-xl font-bold">{ar.totalTransactions}</Text>
        <Pressable onPress={hide} hitSlop={20} className="bg-gray-100 p-2 rounded-full active:bg-gray-200">
          <Icon name="close" size={20} color="#374151" />
        </Pressable>
      </View>

      {total.isLoading ? (
        <View className="items-center py-6">
          <ActivityIndicator color="#2563EB" />
        </View>
      ) : (
        <View className="py-2 gap-2">
          <View className="flex-row items-center justify-between">
            <Text className="text-sm text-gray-500">{ar.purchases}</Text>
            <Text className="text-lg font-bold text-green-600">
              {formatPrice(total.data?.purchases ?? 0)} <Text className="text-xs font-normal text-green-500">{ar.egp}</Text>
            </Text>
          </View>

          <View className="flex-row items-center justify-between">
            <Text className="text-sm text-gray-500">{ar.returns}</Text>
            <Text className="text-lg font-bold text-red-600">
              {formatPrice(total.data?.returns ?? 0)} <Text className="text-xs font-normal text-red-500">{ar.egp}</Text>
            </Text>
          </View>

          <View className="border-t border-gray-200 my-1" />

          <View className="flex-row items-center justify-between">
            <Text className="text-sm font-bold text-gray-900">{ar.net}</Text>
            <Text className={`text-2xl font-black ${netColor}`}>
              {formatPrice(net)} <Text className={`text-sm font-normal ${netColor}`}>{ar.egp}</Text>
            </Text>
          </View>

          <Text className="text-xs text-gray-400 text-center mt-1">
            {ar.transactionsCount}: {total.data?.count ?? 0}
          </Text>
        </View>
      )}
    </ModalWrapper>
  )
}
